import { defineStore } from 'pinia';
import {
  ref, computed, watch, nextTick,
} from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { Label } from 'radix-vue';

import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';

export const useEditorStore = defineStore('editor', () => {
  const mode = ref('edit');
  const currentSample = ref({
    image: null,
    imageObj: null,
    annotations: [],
    loaded: false,
  });
  const prevInternalAnnotations = ref(null);
  const sequences = ref([]);
  const changedAnnotations = ref({});
  const labels = ref([]);
  const annotationSets = ref([]);
  const annotationSetToEdit = ref(null);
  const imageDimensions = ref({});
  const canvasScale = ref(1);
  const selectedAnnotationIdentifiers = ref([]);
  const newAnnotationsSavePending = ref(false);

  const sequenceId = ref(null);
  watch(() => currentSample.value?.imageObj?.sequence_id, (newVal) => {
    if (currentSample.value?.imageObj) {
      sequenceId.value = newVal ?? null;
    }
  });
  const isSequence = computed(() => Boolean(currentSample.value?.imageObj?.sequence_id));

  const imageGroupId = computed(() => currentSample.value?.imageObj?.group_id);

  const datasetId = computed(() => {
    if (currentSample.value?.imageObj?.dataset_id) {
      return currentSample.value?.imageObj?.dataset_id;
    }
    return null;
  });

  const isVideo = computed(() => currentSample.value?.imageObj?.type === 'video');

  const annotationIdentifierMap = computed(() => {
    const newAnnotationIdentifierMap = {};
    currentSample.value.annotations.forEach((anno, i) => {
      newAnnotationIdentifierMap[anno.id] = anno;
    });
    return newAnnotationIdentifierMap;
  });

  const selectedAnnotations = computed(() => selectedAnnotationIdentifiers.value.map((identifier) => annotationIdentifierMap.value[identifier]));

  const selectedAnnotationInfo = computed(() => {
    if (selectedAnnotationIdentifiers.value.length > 0) {
      return annotationIdentifierMap.value[selectedAnnotationIdentifiers.value[0]];
    }
    return null;
  });

  function $reset() {
    mode.value = 'edit';
    currentSample.value = {
      image: null,
      imageObj: null,
      annotations: [],
      loaded: false,
    };
    prevInternalAnnotations.value = null;
    sequences.value = [];
    labels.value = [];
    annotationSets.value = [];
    annotationSetToEdit.value = null;
    imageDimensions.value = {};
    canvasScale.value = 1;
    selectedAnnotationIdentifiers.value = [];
    sequenceId.value = null;
    copiedAnnotations.value = [];
  }

  function clearSample() {
    currentSample.value = {
      image: null,
      imageObj: null,
      annotations: [],
      loaded: false,
    };
  }

  function handleImageLoaded(dimensions) {
    imageDimensions.value = dimensions;
  }

  function selectAnnotation(annotation_id) {
    if (annotation_id) {
      selectedAnnotationIdentifiers.value = [annotation_id];
    }
  }

  function handleUndo() {
    if (prevInternalAnnotations.value !== null) {
      const previousAnnotations = JSON.parse(JSON.stringify(prevInternalAnnotations.value));
      setCurrentAnnotations(previousAnnotations);
      nextTick(() => { prevInternalAnnotations.value = null; });
    }
  }

  function handleRevertChanges() {
    const originalAnnotations = JSON.parse(JSON.stringify(currentSample.value.imageObj?.annotations || []));
    setCurrentAnnotations(originalAnnotations);
    nextTick(() => { prevInternalAnnotations.value = null; });
    changedAnnotations.value = {};
    newAnnotationsSavePending.value = false;
  }

  // Copy and paste annotation
  const copiedAnnotations = ref([]);
  const hasCopiedAnnotations = computed(() => {
    if (copiedAnnotations.value.length > 0) {
      return true;
    }
    return false;
  });
  function handleCopyAnnotations() {
    copiedAnnotations.value = [...JSON.parse(JSON.stringify(selectedAnnotations.value)).map((item) => {
      item.id = uuidv4();
      item.reviewStatus = 'verified';
      return item;
    })];
  }
  function handlePasteAnnotations() {
    setCurrentAnnotations([...currentSample.value.annotations, ...copiedAnnotations.value]);
  }

  function copyAnnotationsObject(annotations) {
    if (!annotations) {
      return [];
    }
    const copiedAnnotations = JSON.parse(JSON.stringify(annotations));
    return copiedAnnotations;
  }

  function setCurrentAnnotations(annotations) {
    if (currentSample.value.annotations) {
      if (!annotations) {
        currentSample.value.annotations = [];
      }
      currentSample.value.annotations = JSON.parse(JSON.stringify(annotations));
    }
  }

  function relabelSelectedAnnotations(label) {
    if (mode.value !== 'edit' || label?.index == null) return;

    const selectedIds = new Set(selectedAnnotationIdentifiers.value);
    const hasChanges = currentSample.value.annotations.some(
      (annotation) => selectedIds.has(annotation.id) && annotation.label_index !== label.index,
    );
    if (!hasChanges) return;

    setCurrentAnnotations(currentSample.value.annotations.map((annotation) => {
      if (!selectedIds.has(annotation.id) || annotation.label_index === label.index) return annotation;
      return {
        ...annotation,
        label_index: label.index,
        label_id: label.id,
        save_pending: true,
        status: annotation.status || 'updated',
      };
    }));
  }

  function addToCurrentAnnotations(annotations) {
    //Add annotation to changed annotation
    for (let k=0;k<annotations.length;k++) {
      annotations[k]['status']='new';
      annotations[k]['save_pending']=true;
      changedAnnotations.value[annotations[k].id]=annotations[k];
    }

    const annotationsForCurrentImage = annotations.filter(
      (annotation) => annotation.image_id === currentSample.value.imageObj?.id,
    );
    setCurrentAnnotations([
      ...(currentSample.value.annotations || []),
      ...annotationsForCurrentImage,
    ]);
  }

  function clearChangedAnnotationsForImage(imageId) {
    if (!imageId) {
      return;
    }

    const retained = {};
    Object.entries(changedAnnotations.value).forEach(([annotationId, annotation]) => {
      if (annotation?.image_id !== imageId) {
        retained[annotationId] = annotation;
      }
    });
    changedAnnotations.value = retained;
  }

  async function PerformKeyFrameInterpolation(keyframeSets) {

    const kfSets=keyframeSets.value;

    // Get Image List
    // Assumption: Images list is ordered by frame number. So it can be used to get the image id from the frame number in the keyframes  
    const params= {
      only_ids:true,
      sort_by: 'frame_no',
      images_filter: {
        sequence_id:sequenceId.value  
      }
    }

    let images=[];
    const dataConnect = new DatastoreConnect();

    const resp = await dataConnect.getFilteredImages(params);
    if (resp['result']) {
      images=resp['result'];
    } else if(resp['error']) {
      console.log(resp['error'])
    }

    if (images.length==0) {
      console.log('No images in sequence')
      return false;
    }



    let anns=[];

    for(let s=0;s<kfSets.length;s++) {
      const keyframes=kfSets[s].keyframes;
      if (keyframes.length<2) continue;

      const entries = Object.entries(keyframes);
      const obj_ref= uuidv4();


      for (let k=0;k<entries.length-1;k++) {
        const [sf, kf] = entries[k];
        const [ef, next_kf] = entries[k+1];
        const ann1=kf.annotation;
        const ann2=next_kf.annotation;
        const start_frame=parseInt(sf);
        const end_frame=parseInt(ef);


        const n=end_frame-start_frame;
        const x2d=(ann2.x-ann1.x)/n;
        const y2d=(ann2.y-ann1.y)/n;
        const w2d=(ann2.w-ann1.w)/n;
        const h2d=(ann2.h-ann1.h)/n;


        const has3dData = ann1.data_json && ann2.data_json;
        const x3d = has3dData ? (ann2.data_json.x-ann1.data_json.x)/n : 0;
        const y3d = has3dData ? (ann2.data_json.y-ann1.data_json.y)/n : 0;
        const z3d = has3dData ? (ann2.data_json.z-ann1.data_json.z)/n : 0;
        const dx3d = has3dData ? (ann2.data_json.dx-ann1.data_json.dx)/n : 0;
        const dy3d = has3dData ? (ann2.data_json.dy-ann1.data_json.dy)/n : 0;
        const dz3d = has3dData ? (ann2.data_json.dz-ann1.data_json.dz)/n : 0;
        


        let m=0;
        for(let frame=start_frame+1;frame<end_frame;frame++) {
          m=m+1;
          const frameIndex=frame-1;
          const newAnn= {
            id: uuidv4(),
            x:ann1.x+x2d*m,
            y:ann1.y+y2d*m,
            w:ann1.w+w2d*m,
            h:ann1.h+h2d*m,
            score:1,
            type:'box',
            annotation_set_id:ann1.annotation_set_id,
            label_id:ann1.label_id,
            label_index:ann1.label_index,
            label_name:ann1.label_name,
            image_id:images[frameIndex],
            object_reference:obj_ref,
            save_pending:true,

          }

          if (has3dData) {
            newAnn.data_json = {
              x: ann1.data_json.x + x3d * m,
              y: ann1.data_json.y + y3d * m,
              z: ann1.data_json.z + z3d * m,
              dx: ann1.data_json.dx + dx3d * m,
              dy: ann1.data_json.dy + dy3d * m,
              dz: ann1.data_json.dz + dz3d * m,
            };
          }

          
          anns.push(newAnn);
        }
      }
    }

    if (anns.length>0) {
      newAnnotationsSavePending.value=true;
      keyframeSets.value=[];
      addToCurrentAnnotations(anns);
    }

    return anns.length > 0;
  }





  return {
    mode,
    currentSample,
    prevInternalAnnotations,
    sequences,
    imageGroupId,
    datasetId,
    sequenceId,
    isSequence,
    isVideo,
    labels,
    annotationSets,
    annotationSetToEdit,
    imageDimensions,
    canvasScale,
    selectedAnnotationIdentifiers,
    annotationIdentifierMap,
    selectedAnnotations,
    selectedAnnotationInfo,
    copiedAnnotations,
    hasCopiedAnnotations,
    setCurrentAnnotations,
    relabelSelectedAnnotations,
    addToCurrentAnnotations,
    clearChangedAnnotationsForImage,
    clearSample,
    handleImageLoaded,
    selectAnnotation,
    handleUndo,
    handleRevertChanges,
    handleCopyAnnotations,
    handlePasteAnnotations,
    copyAnnotationsObject,
    PerformKeyFrameInterpolation,
    changedAnnotations,
    newAnnotationsSavePending,
    $reset,
    
  };
});
